// src/components/digital_twin/twin_engine/useAssetTwin.js
// Loads and keeps fresh every backend source for the asset open in the twin.
//   • parallel fetch, aborted when the asset changes / the drawer closes
//   • refresh every REFRESH_MS while live and the tab is visible; paused in replay
//   • websocket: digital_twin_state (health / readings for this asset), well_intel_update (wells)
//   • replay: /ai/historical-twin-state at the page's timeline position
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import webSocketService from '../../../services/websocketService';
import { fetchAssetTwin, fetchHistoricalTwin, normHealth } from './twinData';
import { profileOf } from './assetProfile';

const REFRESH_MS = 60000;

export default function useAssetTwin({ asset, sensors = [], isLive = true, historicalTimestamp = null }) {
  const profile = useMemo(() => profileOf(asset), [asset]);
  const [sources, setSources] = useState({});
  const [loading, setLoading] = useState(false);
  const [liveHealth, setLiveHealth] = useState(null);
  const [historical, setHistorical] = useState(null);
  const [lastPush, setLastPush] = useState(null);
  const abortRef = useRef(null);
  const assetId = asset?.id != null ? String(asset.id) : null;
  const sensorsKey = sensors.map((s) => s.device_id ?? s.id).join(',');
  const sensorsRef = useRef(sensors); sensorsRef.current = sensors;
  const assetRef = useRef(asset); assetRef.current = asset;

  const load = useCallback(async () => {
    const a = assetRef.current;
    if (!a?.id) return;
    abortRef.current?.abort();
    const ctrl = new AbortController(); abortRef.current = ctrl;
    setLoading(true);
    try {
      const out = await fetchAssetTwin({ asset: a, profile: profileOf(a), sensors: sensorsRef.current, signal: ctrl.signal });
      if (!ctrl.signal.aborted) setSources(out);
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, []);

  // initial + periodic (live only)
  useEffect(() => {
    setSources({}); setLiveHealth(null); setHistorical(null);
    if (!assetId) return undefined;
    load();
    if (!isLive) return () => abortRef.current?.abort();
    const t = setInterval(() => { if (document.visibilityState !== 'hidden') load(); }, REFRESH_MS);
    return () => { clearInterval(t); abortRef.current?.abort(); };
  }, [assetId, sensorsKey, isLive, load]);

  // replay snapshot
  useEffect(() => {
    if (isLive || !historicalTimestamp || !assetId) { setHistorical(null); return undefined; }
    const ctrl = new AbortController();
    fetchHistoricalTwin(assetId, historicalTimestamp, ctrl.signal).then((r) => { if (!ctrl.signal.aborted) setHistorical(r); });
    return () => ctrl.abort();
  }, [isLive, historicalTimestamp, assetId]);

  // live pushes
  useEffect(() => {
    if (!assetId || !isLive) return undefined;
    const onTwin = (msg) => {
      const p = msg?.payload ?? msg?.data ?? msg;
      if (!p || String(p.asset_id) !== assetId) return;
      const h = normHealth({ health_score: p.health_score, health_status: p.health_status, trend: p.health?.trend });
      if (h) setLiveHealth({ ...h, at: Date.now() });
      setLastPush(Date.now());
    };
    const onWell = (msg) => {
      const p = msg?.payload ?? msg?.data ?? msg;
      if (p?.well && String(p.well.id) === assetId) {
        setSources((s) => ({ ...s, wellIntel: { status: 'ok', data: p.well, at: Date.now(), ms: 0 } }));
        setLastPush(Date.now());
      }
    };
    const offA = webSocketService.subscribe?.('digital_twin_state', onTwin);
    const offB = webSocketService.subscribe?.('well_intel_update', onWell);
    return () => {
      if (typeof offA === 'function') offA(); else webSocketService.unsubscribe?.('digital_twin_state', onTwin);
      if (typeof offB === 'function') offB(); else webSocketService.unsubscribe?.('well_intel_update', onWell);
    };
  }, [assetId, isLive]);

  const health = liveHealth || sources.health?.data || null;
  return { profile, sources, health, historical, loading, refresh: load, lastPush };
}
